import * as React from "react"
import { MAX_ALFRED_BULK_DEVICE_UPDATES } from "@/types/alfred-device"

export const MAX_ALFRED_BULK_SELECTION = MAX_ALFRED_BULK_DEVICE_UPDATES

type SelectedDeviceEntry = readonly [id: number, deviceId: string]

export function useAlfredBulkSelection(
  scopeKey: string,
  onLimitReached?: () => void,
) {
  const [selectedDevices, setSelectedDevices] = React.useState<Map<number, string>>(
    new Map(),
  )
  const previousScopeKey = React.useRef(scopeKey)
  const currentScopeKey = React.useRef(scopeKey)

  React.useLayoutEffect(() => {
    currentScopeKey.current = scopeKey
  }, [scopeKey])

  React.useEffect(() => {
    if (previousScopeKey.current === scopeKey) return
    previousScopeKey.current = scopeKey
    setSelectedDevices(new Map())
  }, [scopeKey])

  const clear = React.useCallback(() => {
    setSelectedDevices(new Map())
  }, [])

  const replaceForScope = React.useCallback((
    expectedScopeKey: string,
    entries: ReadonlyArray<SelectedDeviceEntry>,
  ) => {
    if (currentScopeKey.current !== expectedScopeKey) return
    setSelectedDevices(new Map(entries.slice(0, MAX_ALFRED_BULK_SELECTION)))
    if (entries.length > MAX_ALFRED_BULK_SELECTION) onLimitReached?.()
  }, [onLimitReached])

  const toggle = React.useCallback((id: number, deviceId: string) => {
    if (!selectedDevices.has(id) && selectedDevices.size >= MAX_ALFRED_BULK_SELECTION) {
      onLimitReached?.()
      return
    }
    setSelectedDevices((previous) => {
      const next = new Map(previous)
      if (next.has(id)) next.delete(id)
      else if (next.size < MAX_ALFRED_BULK_SELECTION) next.set(id, deviceId)
      return next
    })
  }, [onLimitReached, selectedDevices])

  const toggleAll = React.useCallback((
    entries: ReadonlyArray<SelectedDeviceEntry>,
    checked: boolean,
  ) => {
    const next = new Map(selectedDevices)
    if (!checked) {
      entries.forEach(([id]) => next.delete(id))
      setSelectedDevices(next)
      return
    }

    let limitReached = false
    for (const [id, deviceId] of entries) {
      if (next.has(id)) continue
      if (next.size >= MAX_ALFRED_BULK_SELECTION) {
        limitReached = true
        break
      }
      next.set(id, deviceId)
    }
    setSelectedDevices(next)
    if (limitReached) onLimitReached?.()
  }, [onLimitReached, selectedDevices])

  return {
    selectedDevices,
    clear,
    replaceForScope,
    toggle,
    toggleAll,
  }
}
